import type { ChatModelRunResult } from "@assistant-ui/react";
import { appEvents } from "@/lib/events";
import { HYBRID_WAIT_TOOL_NAMES } from "@/lib/toolConstants";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { usePraetorRevisionStore } from "./stores/praetorRevisionStore";
import { ContextWindowSnapshot, useContextBudgetStore } from "./stores/contextBudgetStore";
import { useApprovalStore } from "./stores/approvalStore";
import { useReconnectStateStore } from "./stores/reconnectStateStore";
import { openPreviewInSidebar } from "@/lib/threadDocumentOps";
import type { UnstableDataEntry } from "@/lib/assistantUiTypes";
import i18n from "@/lib/i18n";
import { ChatStreamError } from "@/lib/chatStreamError";
import { ClientLogger } from "@/lib/clientLogger";

// ─── SSE resume cursor ───────────────────────────────────────────────────────
// Stashes the latest queue event id per thread so `/chat/resume` can send
// `Last-Event-ID` and skip already-rendered frames. Scoped to the current page
// load via sessionStorage; cleared on full refresh by `clearLastEventId()`.

const CURSOR_STORAGE_PREFIX = "praetor:sse:lastEventId:";

// Rate-limit countdown stash. The data-rate-limit-countdown DataEvent
// arrives immediately before the ErrorEvent on a 429/529, so the error arm
// can interpolate the retry-after seconds into the localized message.
// Module-scoped because event ordering within one SSE stream is serial; there's
// no race between a countdown event and the immediately-following error event.
let lastRateLimitInfo: { code: string; scope: string; retryAfterSec: number; message: string | null } | null = null;

export type ThinkingMetadata = {
  // 3–4 word Czech task title produced by the thinking model — drives the
  // process-window header text. Nullable on pre-2026-05-19 history rows where the
  // field did not yet exist; UI falls back to a generic "Analysis" label then.
  taskTitle?: string | null;
};

function rememberLastEventId(threadId: string, eventId: string) {
  try {
    sessionStorage.setItem(CURSOR_STORAGE_PREFIX + threadId, eventId);
  } catch {
    // sessionStorage can be blocked (private mode, storage quota). The cost of
    // a cursor miss is a full replay on reconnect — not a correctness issue.
  }
}

export function getLastEventId(threadId: string): string | undefined {
  try {
    return sessionStorage.getItem(CURSOR_STORAGE_PREFIX + threadId) ?? undefined;
  } catch {
    return undefined;
  }
}

export function clearLastEventId(threadId: string) {
  try {
    sessionStorage.removeItem(CURSOR_STORAGE_PREFIX + threadId);
  } catch {
    // ignore
  }
}

export type StreamDeps = {
  readJsonSse: (
    res: Response,
    abortSignal: AbortSignal,
    onEventId?: (id: string) => void,
  ) => AsyncGenerator<ServerStreamEvent>;
  safeJsonParse: (s: string) => unknown | undefined;
};

export type ToolCallRequestData = {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  messageId: string;
  timeoutMs: number;
  status: "pending" | "approved" | "denied";
};

export type DocumentCitationData = {
  docId: string;
  name?: string;
  pages?: string;
  snippet?: string;
};

/**
 * One web-search citation site delivered via the data-web-source SSE event
 * (or replayed from /threads/{id}/messages on cold load). Per-citation, not
 * per-source URL — the same URL cited from two paragraphs becomes two records,
 * each carrying the snippet that pins it to its prose location. Footer rendering
 * dedups by URL; the displayed source number is the position of the URL in the
 * deduped list.
 */
export type WebSourceData = {
  url: string;
  title?: string | null;
  citedText?: string | null;
};

export type OnDocumentCitationDetected = (
  citation: DocumentCitationData,
  originatingThreadId: string | undefined,
) => void;

/**
 * Fired on data-document-ready so the host can auto-open the generated doc in
 * the PDF sidebar. Same staleness gate as OnDocumentCitationDetected: if the
 * originatingThreadId differs from the current live thread, the host should
 * drop the event silently (a thread-switch raced ahead of stream completion).
 */
export type OnDocumentReady = (
  documentId: string,
  title: string | undefined,
  originatingThreadId: string | undefined,
) => void;

export async function* streamChatToRunResults(
  res: Response,
  abortSignal: AbortSignal,
  deps: StreamDeps,
  onDocumentCitation?: OnDocumentCitationDetected,
  threadId?: string,
  onDocumentReady?: OnDocumentReady,
): AsyncGenerator<ChatModelRunResult, void, unknown> {
  const { readJsonSse, safeJsonParse } = deps;

  // Cursor callback — runs for every SSE frame that carried an `id:` line.
  // We only store if we know the thread; anonymous streams can't be resumed.
  const onEventId = threadId
    ? (eventId: string) => {
        rememberLastEventId(threadId, eventId);
      }
    : undefined;

  // ─── rAF-gated emission ─────────────────────────────────────────────────────
  // ALL non-terminal events yield at most once per animation frame (~16ms).
  // During live streaming, events arrive with natural latency (>16ms apart) so
  // the throttle never fires. During resume replay (burst of buffered events),
  // this prevents 50+ rapid yields that would trigger React's max-update-depth
  // error (#185). Terminal events (finish, error returns) bypass the gate.
  // The `finally { yield emit() }` block guarantees the last state is flushed.
  const FRAME_BUDGET_MS = 16;
  let lastYieldAt = 0;

  /** Returns true if enough time has passed to yield a new snapshot to React. */
  const shouldYield = (): boolean => {
    const now = performance.now();
    if (now - lastYieldAt >= FRAME_BUDGET_MS) {
      lastYieldAt = now;
      return true;
    }
    return false;
  };

  const parts: UiPart[] = [];
  let textIndex: number | null = null;
  let reasoningIndex: number | null = null;

  const toolPartIndexById = new Map<string, number>();
  const toolArgsTextById = new Map<string, string>();
  const pendingToolParts = new Map<string, UiPart & { type: "tool-call" }>();
  // Sub-agent lanes indexed by runId → position in parts[].
  const subAgentLaneIndexByRunId = new Map<string, number>();
  const citationsAccumulated: DocumentCitationData[] = [];
  const webSourcesAccumulated: WebSourceData[] = [];
  // Insertion-order set so the inline footer cards on the streaming message
  // render in the order the LLM produced them, matching the persisted
  // GeneratedDocumentIds[] column on the assistant row.
  const generatedDocumentIds = new Set<string>();
  const praetorRevisions = new Map<string, string | undefined>();
  let capturedModelId: string | null = null;
  let capturedBackendMessageId: string | null = null;
  let capturedStartedAtUtc: string | null = null;
  let capturedUsage: {
    inputTokens?: number;
    outputTokens?: number;
    reasoningTokens?: number;
    cachedTokens?: number;
    totalTokens?: number;
    estimatedCostUsd?: number | null;
    estimatedCostDisplay?: string | null;
    totalDurationMs?: number | null;
  } | null = null;

  // NOTE: this is what tool-fallback typically cares about. Keep common statuses
  // referentially stable so assistant-ui part/status selectors don't see a fresh
  // object on every text frame.
  const RUNNING_STATUS = Object.freeze({ type: "running" } as const);
  const REQUIRES_TOOL_ACTION_STATUS = Object.freeze({ type: "requires-action", reason: "tool-call" } as const);
  const INCOMPLETE_CANCELLED_STATUS = Object.freeze({ type: "incomplete", reason: "cancelled" } as const);
  const INCOMPLETE_OTHER_STATUS = Object.freeze({ type: "incomplete", reason: "other" } as const);
  let messageStatus: { type: string; reason?: string } | undefined;
  let lastEmittedStatusSignature: string | undefined;
  let lastEmittedDataSignature = "";

  const getStatusSignature = () =>
    messageStatus ? `${messageStatus.type}:${messageStatus.reason ?? ""}` : "";

  // Set when the stream ends with a terminal state that the RetryCard
  // should see immediately (without a cold-load). Injected into unstable_data
  // by emit() so the button reads it from message.metadata.
  let pendingTerminalState: string | null = null;

  // Set when a retryable error occurs (rate-limit, overloaded, stream-interrupted).
  // Injected into unstable_data by emit() so the RetryCard can display the
  // server-localized message and countdown timer.
  let pendingRetryInfo: { code: string; message: string | null; retryAfterSec: number } | null = null;

  // Active MCP elicitations (typically 0 or 1 per turn — rate-cap is 10/min). Kept
  // as a Map so a later turn-event can RESOLVE an in-flight entry without rebuilding
  // the unstable_data array; in practice we only ADD here and the user response POST
  // unmounts the card via the parent re-render that flushes the next emit().
  const activeMcpElicitations = new Map<string, { message: string; schema: unknown; context: unknown }>();

  const buildUnstableDataSignature = (): string => {
    const usage = capturedUsage
      ? [
          capturedUsage.inputTokens ?? "",
          capturedUsage.outputTokens ?? "",
          capturedUsage.reasoningTokens ?? "",
          capturedUsage.cachedTokens ?? "",
          capturedUsage.totalTokens ?? "",
          capturedUsage.estimatedCostUsd ?? "",
          capturedUsage.estimatedCostDisplay ?? "",
          capturedUsage.totalDurationMs ?? "",
        ].join(",")
      : "";
    const retry = pendingRetryInfo
      ? `${pendingRetryInfo.code}:${pendingRetryInfo.message ?? ""}:${pendingRetryInfo.retryAfterSec}`
      : "";
    const elicitations = [...activeMcpElicitations.entries()]
      .map(([id, entry]) => `${id}:${entry.message}`)
      .join("|");
    return [
      citationsAccumulated.length,
      webSourcesAccumulated.length,
      capturedModelId ?? "",
      capturedBackendMessageId ?? "",
      capturedStartedAtUtc ?? "",
      usage,
      pendingTerminalState ?? "",
      retry,
      [...generatedDocumentIds].join("|"),
      [...praetorRevisions.entries()].map(([id, title]) => `${id}:${title ?? ""}`).join("|"),
      elicitations,
    ].join(";");
  };

  const emit = (): ChatModelRunResult => {
    const statusSignature = getStatusSignature();
    const includeStatus = statusSignature !== lastEmittedStatusSignature;
    lastEmittedStatusSignature = statusSignature;

    const dataSignature = buildUnstableDataSignature();
    const includeMetadata = dataSignature !== lastEmittedDataSignature;
    lastEmittedDataSignature = dataSignature;

    const unstableData: UnstableDataEntry[] = [];
    if (includeMetadata) {
      if (citationsAccumulated.length)
        unstableData.push({ type: "citations", citations: [...citationsAccumulated] });
      if (webSourcesAccumulated.length)
        unstableData.push({ type: "web-sources", sources: [...webSourcesAccumulated] });
      if (capturedModelId)
        unstableData.push({ type: "model", modelId: capturedModelId });
      if (capturedUsage)
        unstableData.push({ type: "usage", ...capturedUsage });
      if (pendingTerminalState)
        unstableData.push({ type: "terminal-state", state: pendingTerminalState });
      if (pendingRetryInfo)
        unstableData.push({ type: "retry-info", ...pendingRetryInfo });
      if (capturedBackendMessageId)
        unstableData.push({ type: "backend-message-id", id: capturedBackendMessageId });
      if (capturedStartedAtUtc)
        unstableData.push({ type: "timing", startedAtUtc: capturedStartedAtUtc, totalDurationMs: capturedUsage?.totalDurationMs ?? null });
      if (generatedDocumentIds.size)
        unstableData.push({ type: "generated-documents", ids: [...generatedDocumentIds] });
      if (praetorRevisions.size) {
        const revisions = [...praetorRevisions.entries()].map(([id, title]) => ({ id, title }));
        unstableData.push({ type: "praetor-revisions", revisions });
      }
      for (const [id, entry] of activeMcpElicitations) {
        unstableData.push({ type: "mcp-elicitation", id, ...entry });
      }
    }

    return {
      content: parts as any,
      ...(includeStatus && messageStatus ? { status: messageStatus as any } : {}),
      ...(includeMetadata ? { metadata: { unstable_data: unstableData as any } } : {}),
    };
  };

  const ensureTextPart = () => {
    if (textIndex == null) {
      parts.push({ type: "text", text: "" });
      textIndex = parts.length - 1;
    }
  };

  const ensureReasoningPart = () => {
    if (reasoningIndex == null) {
      parts.push({ type: "reasoning", text: "" });
      reasoningIndex = parts.length - 1;
    }
  };

  const closeReasoning = () => {
    reasoningIndex = null;
  };

  const upsertToolPart = (toolCallId: string, patch: ToolCallPatch) => {
    const existingIdx = toolPartIndexById.get(toolCallId);
    if (existingIdx != null) {
      const current = parts[existingIdx] as UiToolCallPart;
      parts[existingIdx] = { ...current, ...patch };
      return;
    }

    const toolName =
      patch.toolName ??
      pendingToolParts.get(toolCallId)?.toolName ??
      "tool";

    const staged: UiPart & { type: "tool-call" } = {
      type: "tool-call",
      toolCallId,
      toolName,
      argsText:
        patch.argsText ??
        pendingToolParts.get(toolCallId)?.argsText ??
        "",
      result:
        patch.result ??
        pendingToolParts.get(toolCallId)?.result ??
        undefined,
      state:
        patch.state ??
        pendingToolParts.get(toolCallId)?.state ??
        "partial",
      mcpServerName:
        patch.mcpServerName ??
        pendingToolParts.get(toolCallId)?.mcpServerName ??
        undefined,
      toolTitle:
        patch.toolTitle ??
        pendingToolParts.get(toolCallId)?.toolTitle ??
        undefined,
      progressMessage:
        patch.progressMessage ??
        pendingToolParts.get(toolCallId)?.progressMessage ??
        undefined,
      progressValue:
        patch.progressValue ??
        pendingToolParts.get(toolCallId)?.progressValue ??
        undefined,
      progressTotal:
        patch.progressTotal ??
        pendingToolParts.get(toolCallId)?.progressTotal ??
        undefined,
      isFinalProgress:
        patch.isFinalProgress ??
        pendingToolParts.get(toolCallId)?.isFinalProgress ??
        false,
    };

    // Always materialize immediately so the ToolGroup renders right away.
    // This ensures the CSS can suppress the pulsing dot while tools are running.
    parts.push(staged);
    toolPartIndexById.set(toolCallId, parts.length - 1);
    pendingToolParts.delete(toolCallId);
  };

  try {
  for await (const evt of readJsonSse(res, abortSignal, onEventId)) {
    if (evt.type === "start") {
      capturedModelId = evt.modelId ?? capturedModelId;
      capturedBackendMessageId = evt.messageId ?? capturedBackendMessageId;
      capturedStartedAtUtc = evt.startedAtUtc ?? capturedStartedAtUtc;
      continue;
    }

    if (evt.type === "mcp-server-unavailable") {
      const servers = evt.servers;
      appEvents.emit("mcp-server-unavailable", servers);
      continue;
    }

    if (evt.type === "data-mcp-elicitation") {
      // praetor-mcp v5.7.0 scope-filter: server is asking the lawyer whether to
      // approve an out-of-scope tool call. Park the entry on unstable_data so
      // <MessageMcpElicitation /> can render an inline approval card. messageStatus
      // is NOT flipped to "requires-action" — the agent loop is still running on
      // the server side (the dispatcher just awaits the response TCS), so flipping
      // would freeze the surrounding UI; the card is rendered as an inline overlay
      // instead and the rest of the assistant message continues to stream beneath it.
      activeMcpElicitations.set(evt.id, {
        message: evt.message,
        schema: evt.schema,
        context: evt.context,
      });
      yield emit();
      continue;
    }

    if (evt.type === "data-mcp-elicitation-resolved") {
      // Server notifies that the elicitation TCS resolved (accept/decline/cancel/timeout).
      // Remove from the active map so the card disappears on the next emit().
      activeMcpElicitations.delete(evt.id);
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "data-tool-call-request") {
      const data = evt.data;
      const toolCallId = data.id;

      // For frontend-input tools the backend suppresses the ToolInput* streaming lifecycle,
      // so this event is the first and only chance to populate toolName + argsText on the
      // part. Passing both in the same upsert avoids a render where `tools:` map routing
      // falls back to "tool"/Fallback or where the interactive form has no args to draw.
      // For approval-gated MCP tools the input events fired first — merging name/argsText
      // again here is a no-op.
      const argsText =
        data.arguments != null
          ? typeof data.arguments === "string"
            ? data.arguments
            : JSON.stringify(data.arguments)
          : undefined;

      upsertToolPart(toolCallId, {
        state: "pending",
        ...(data.name ? { toolName: data.name } : {}),
        ...(argsText != null ? { argsText } : {}),
      });

      // Hybrid-wait tools (ask/ask_open) keep messageStatus as "running" so
      // thread.isRunning stays true, the composer remains disabled, and there's no
      // flash between onSubmitted() and the data-tool-call-result-inline SSE event.
      // request_commit_approval and other tools still flip to requires-action
      // because they use the blocking body-bearing path with a different lifecycle.
      if (!HYBRID_WAIT_TOOL_NAMES.has(data.name ?? "")) {
        messageStatus = REQUIRES_TOOL_ACTION_STATUS;
      }

      yield emit();
      continue;
    }

    if (evt.type === "data-tool-call-canceled") {
      ClientLogger.info("Tool call cancelled", {
        component: "chatModelAdapters", metadata: { toolCallId: evt.data?.id },
      });

      // Fixed: union was { toolCallId } but server sends .id
      const toolCallId = evt.data.id;

      upsertToolPart(
        toolCallId,
        { result: { cancelled: true }, state: "complete" }
      );

      messageStatus = INCOMPLETE_CANCELLED_STATUS;

      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "data-tool-call-request-update") {
      const toolCallId = evt.data.id;
      const isApproved = evt.data.status === "approved";

      upsertToolPart(
        toolCallId,
        { result: "", state: "complete" }
      );

      messageStatus = isApproved
        ? RUNNING_STATUS
        : INCOMPLETE_OTHER_STATUS;

      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "data-tool-call-result-inline") {
      // Hybrid in-loop wait: the user answered and the middleware resumed.
      // Flip the tool part to complete and restore running status so the
      // assistant continues typing in the same message.
      upsertToolPart(evt.data.toolCallId, {
        result: evt.data.answer,
        state: "complete",
      });
      messageStatus = RUNNING_STATUS;
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "data-document-citation") {
      const citation = evt.data as DocumentCitationData;
      citationsAccumulated.push(citation);
      onDocumentCitation?.(citation, threadId);
      continue;
    }

    if (evt.type === "data-web-source") {
      const src = evt.data as WebSourceData;
      if (src && typeof src.url === "string" && src.url.length > 0) {
        webSourcesAccumulated.push({
          url: src.url,
          title: src.title ?? null,
          citedText: src.citedText ?? null,
        });
      }
      continue;
    }

    if (evt.type === "data-document-rendering") {
      if (evt.documentId != null) {
        // Track the id on the streaming message so its inline footer card renders
        // immediately. Keeping this on the message metadata (rather than relying
        // solely on the store) ensures the card stays attached to *this* message
        // on re-mount and on cold-load via threadHistoryProvider.
        generatedDocumentIds.add(evt.documentId);
        useGeneratedDocumentStore.getState().setRendering(
          evt.documentId,
          evt.order ?? 0,
          evt.title ?? null,
        );
      }
      continue;
    }

    if (evt.type === "data-document-ready") {
      if (evt.documentId != null) {
        // Cover the case where Ready arrives without a prior Rendering (shouldn't
        // happen today, but cheap to guard).
        generatedDocumentIds.add(evt.documentId);
        const store = useGeneratedDocumentStore.getState();
        if (!store.entries[evt.documentId]) {
          // SSE-ordering anomaly: ready without a prior rendering entry. setReady
          // already constructs a valid fallback entry, this log just surfaces how
          // often the producer is emitting the events out of order.
          ClientLogger.warn("data-document-ready with no prior rendering entry", {
            component: "chatModelAdapters",
            metadata: { documentId: evt.documentId },
          });
        }
        store.setReady(evt.documentId, evt.title ?? null, evt.versionId ?? null, evt.versionNumber ?? null);
        void store.fetchDocuments([evt.documentId]).catch(() => {/* handled internally */});
        // Phase 2 / C1: auto-open the generated doc in the PDF sidebar.
        // Host drops the event if originatingThreadId is stale.
        onDocumentReady?.(evt.documentId, evt.title ?? undefined, threadId);
      }
      continue;
    }

    if (evt.type === "data-pre-approval-review") {
      // Pre-approval review (ADR-042): the assistant called request_commit_approval.
      // Stamp the approval store with the staged preview metadata so the sidebar's
      // ApprovalActionsRow can render on the matching previewId, and auto-open the
      // sidebar to that preview's PDF so the user sees the draft they're approving.
      const previewId = typeof evt.previewId === "string" ? evt.previewId : null;
      const toolCallId = typeof evt.toolCallId === "string" ? evt.toolCallId : null;
      if (previewId && toolCallId && threadId) {
        const genDocId = typeof evt.generatedDocumentId === "string" ? evt.generatedDocumentId : undefined;
        useApprovalStore.getState().setPending({
          previewId,
          toolCallId,
          threadId,
          summary: typeof evt.summary === "string" ? evt.summary : undefined,
          pdfUrl: typeof evt.pdfUrl === "string" ? evt.pdfUrl : `/api/preview/${previewId}/pdf`,
          docxUrl: typeof evt.docxUrl === "string" ? evt.docxUrl : `/api/preview/${previewId}`,
          diffHtmlUrl: typeof evt.diffHtmlUrl === "string" ? evt.diffHtmlUrl : undefined,
          action: typeof evt.action === "string" ? evt.action : undefined,
          documentName: typeof evt.documentName === "string" ? evt.documentName : undefined,
          praetorDocumentId: typeof evt.praetorDocumentId === "string" ? evt.praetorDocumentId : undefined,
          generatedDocumentId: genDocId,
        });
        // Inject a pending version into the document store so the version stepper
        // shows it before the user approves. Uses the previewId as the version id.
        if (genDocId) {
          const pdfUrl = typeof evt.pdfUrl === "string" ? evt.pdfUrl : `/api/preview/${previewId}/pdf`;
          const diffUrl = typeof evt.diffHtmlUrl === "string" ? evt.diffHtmlUrl : undefined;
          useGeneratedDocumentStore.getState().addPendingVersion(genDocId, previewId, pdfUrl, diffUrl);
        }
        void openPreviewInSidebar(previewId, typeof evt.summary === "string" ? evt.summary : undefined, threadId, genDocId);
      }
      continue;
    }

    if (evt.type === "data-praetor-revision-saved") {
      // Main agent saved a new revision of a Praetor case document via save_revision.
      // No thread-side artefact to render — the new content lives in PraetorIS. Surface a toast
      // via the app-event bus; the toast bridge handles localisation + display.
      const docId = typeof evt.praetorDocumentId === "string" ? evt.praetorDocumentId : null;
      if (docId) {
        const title = typeof evt.title === "string" ? evt.title : undefined;
        praetorRevisions.set(docId, title);
        usePraetorRevisionStore.getState().add({ praetorDocumentId: docId, title });
        appEvents.emit("praetor-revision-saved", {
          praetorDocumentId: docId,
          title,
          threadId,
        });
      }
      continue;
    }

    if (evt.type === "data-document-error") {
      if (evt.documentId != null) {
        // Discard `evt.error` — never log server text and never use it for UI.
        // SPA owns its UI strings; telemetry sees only the categorical code.
        ClientLogger.error("Document render failed", {
          component: "chatModelAdapters", metadata: { documentId: evt.documentId, code: "document_render_failed" },
        });
        useGeneratedDocumentStore.getState().setError(evt.documentId, i18n.t("error.code.document_render_failed"));
      }
      continue;
    }

    if (evt.type === "data-show-generated-document") {
      // show_generated_document tool: nudge to open an already-committed doc in sidebar.
      const docId = typeof evt.documentId === "string" ? evt.documentId : null;
      if (docId) {
        const title = typeof evt.title === "string" ? evt.title : undefined;
        onDocumentReady?.(docId, title, threadId);
      }
      continue;
    }

    if (evt.type === "data-show-praetor-document") {
      // show_praetor_document tool: nudge to open a Praetor case document in sidebar.
      const praetorDocId = typeof evt.praetorDocumentId === "string" ? evt.praetorDocumentId : null;
      if (praetorDocId) {
        onDocumentCitation?.({ docId: praetorDocId, name: null, pages: null, snippet: null }, threadId);
      }
      continue;
    }

    if (evt.type === "data-title-generated") {
      const data = evt.data;
      if (data?.title) {
        appEvents.emit("title-generated", { title: data.title, threadId: data.threadId ?? threadId });
      }
      continue;
    }

    if (evt.type === "restored") {
      // Server says the message was already finalized — no streaming content to replay.
      // Signal the history provider to reload from DB; the FinishEvent that follows
      // will terminate the run cleanly. Do NOT yield — would corrupt the content stream.
      if (threadId) appEvents.emit("history-restored", { threadId });
      continue;
    }

    if (evt.type === "data-rate-limit-countdown") {
      const data = evt.data;
      lastRateLimitInfo = {
        code: typeof data.code === "string" ? data.code : "provider_rate_limited",
        scope: typeof data.scope === "string" ? data.scope : "tokens",
        retryAfterSec: typeof data.retryAfterSec === "number" ? data.retryAfterSec : 30,
        message: typeof data.message === "string" ? data.message : null,
      };
      continue;
    }

    // Auto-resume: provider stream broke mid-turn, backend is retrying automatically
    if (evt.type === "data-auto-resume-reconnecting") {
      const data = evt.data as { attempt?: number; maxAttempts?: number };
      const attempt = typeof data.attempt === "number" ? data.attempt : 1;
      if (threadId) {
        useReconnectStateStore.getState().setRetrying(threadId, attempt);
      }
      continue;
    }

    if (evt.type === "data-auto-resume-reconnected") {
      useReconnectStateStore.getState().setIdle(threadId);
      continue;
    }

    if (evt.type === "error") {
      if (evt.code === "quota_exceeded") {
        appEvents.emit("quota-exceeded");
        throw new ChatStreamError("quota_exceeded", i18n.t("error.code.quota_exceeded"), false);
      }
      // Anonymisation NER unavailable: the backend strict-blocked the turn to prevent
      // sending raw PII to the LLM. Surface a dedicated modal (Retry / Disable) and end
      // the turn as Interrupted so assistant-ui shows the standard "ended early" UX.
      if (evt.code === "anonymization_unavailable") {
        if (threadId) appEvents.emit("anonymization-unavailable", { threadId });
        messageStatus = INCOMPLETE_CANCELLED_STATUS;
        pendingTerminalState = "Interrupted";
        if (threadId) appEvents.emit("turn-completed", { threadId });
        yield emit();
        return;
      }
      // Transport-level interruption: the provider's stream dropped mid-turn.
      // Yield gracefully with "incomplete" status + terminal-state + retry-info metadata
      // so assistant-ui shows the RetryCard instead of the error-state Reconnect button.
      if (evt.code === "provider_stream_interrupted") {
        const retryData = lastRateLimitInfo ?? { code: evt.code, message: null, retryAfterSec: 0 };
        lastRateLimitInfo = null;
        pendingRetryInfo = retryData;
        messageStatus = INCOMPLETE_CANCELLED_STATUS;
        pendingTerminalState = "Interrupted";
        if (threadId) appEvents.emit("turn-completed", { threadId });
        yield emit();
        return;
      }
      // Rate-limit codes. Retryable errors yield gracefully; billing exhausted throws.
      if (
        evt.code === "provider_rate_limited" ||
        evt.code === "provider_billing_exhausted" ||
        evt.code === "provider_overloaded"
      ) {
        const info = lastRateLimitInfo;
        lastRateLimitInfo = null;
        if (evt.code === "provider_billing_exhausted") {
          appEvents.emit("quota-exceeded");
          throw new ChatStreamError(
            "provider_billing_exhausted",
            i18n.t("error.code.provider_billing_exhausted"),
            false,
          );
        }
        // Retryable: yield with incomplete status + retry-info metadata
        pendingRetryInfo = info ?? { code: evt.code, message: null, retryAfterSec: 30 };
        messageStatus = INCOMPLETE_CANCELLED_STATUS;
        pendingTerminalState = "Interrupted";
        if (threadId) appEvents.emit("turn-completed", { threadId });
        yield emit();
        return;
      }
      // Any other server error frame is terminal: the turn died on the server,
      // so reconnecting replays nothing. Surface a specific localized message
      // when we have a key for the code (e.g. internal_error), else a clearer
      // generic, and mark it non-recoverable so MessageError shows "Try again"
      // instead of a pointless "Reconnect".
      const code = evt.code ?? "unknown";
      const key = `error.code.${code}`;
      const message = i18n.exists(key) ? i18n.t(key) : i18n.t("error.code.unknown");
      throw new ChatStreamError(code, message, false);
    }

    if (evt.type === "reasoning-start") {
      ensureReasoningPart();
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "reasoning-delta") {
      ensureReasoningPart();
      const delta = evt.delta ?? "";
      if (delta && reasoningIndex != null) {
        (parts[reasoningIndex] as Extract<UiPart, { type: "reasoning" }>).text += delta;
        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "reasoning-end") {
      closeReasoning();
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "reasoning-replace") {
      // Replace the raw JSON thinking text with the clean extracted text.
      // The reasoning part is already closed by reasoning-end; find it by index.
      const targetIdx = reasoningIndex ?? parts.findLastIndex((p) => p.type === "reasoning");
      if (targetIdx >= 0) {
        (parts[targetIdx] as Extract<UiPart, { type: "reasoning" }>).text = evt.text ?? "";
        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "text-delta") {
      const delta = evt.textDelta ?? "";
      if (delta) {
        ensureTextPart();
        if (textIndex != null) {
          (parts[textIndex] as Extract<UiPart, { type: "text" }>).text += delta;
        }
        if (shouldYield()) yield emit();
      }

      continue;
    }

    if (evt.type === "tool-input-start") {
      toolArgsTextById.set(evt.toolCallId, "");
      upsertToolPart(evt.toolCallId, { toolName: evt.toolName, mcpServerName: evt.mcpServerName, toolTitle: evt.toolTitle, state: "partial" });
      messageStatus = RUNNING_STATUS;
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "tool-input-delta") {
      const prev = toolArgsTextById.get(evt.toolCallId) ?? "";
      const next = prev + (evt.inputTextDelta ?? "");
      toolArgsTextById.set(evt.toolCallId, next);

      const parsed = safeJsonParse(next);
      upsertToolPart(evt.toolCallId, {
        argsText: next,
        state: parsed ? "complete" : "partial",
      });

      messageStatus = RUNNING_STATUS;

      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "tool-input-available") {
      upsertToolPart(evt.toolCallId, { state: "pending" });
      messageStatus = RUNNING_STATUS;
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "tool-progress") {
      upsertToolPart(evt.toolCallId, {
        progressMessage: evt.message,
        progressValue: evt.progress,
        progressTotal: evt.total,
        isFinalProgress: evt.isFinal ?? false,
      });
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "tool-result") {
      // Clear non-final progress messages (mid-execution progress like "Processing page 5/10").
      // Final messages (post-execution displayMessage like "Found 8 results") persist.
      const existingIdx = toolPartIndexById.get(evt.toolCallId);
      const existing = existingIdx != null ? (parts[existingIdx] as UiToolCallPart) : undefined;
      const clearProgress = existing && !existing.isFinalProgress;

      upsertToolPart(evt.toolCallId, {
        result: evt.output,
        state: "complete",
        ...(clearProgress ? { progressMessage: undefined, progressValue: undefined, progressTotal: undefined } : {}),
      });

      messageStatus = RUNNING_STATUS;

      if (shouldYield()) yield emit();
      continue;
    }

    // ─── Sub-agent lane events ────────────────────────────────────────────────
    if (evt.type === "sub-agent-start") {
      const lane: SubAgentLanePart = {
        type: "data",
        name: "sub-agent-lane",
        runId: evt.runId,
        conversationId: evt.conversationId,
        agentType: evt.agentType,
        query: evt.query,
        locale: evt.locale,
        status: "running",
        toolCalls: [],
      };
      const idx = parts.length;
      parts.push(lane);
      subAgentLaneIndexByRunId.set(evt.runId, idx);
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "sub-agent-tool-start") {
      const laneIdx = subAgentLaneIndexByRunId.get(evt.runId);
      if (laneIdx != null) {
        const lane = parts[laneIdx] as SubAgentLanePart;
        lane.toolCalls.push({
          toolCallId: evt.toolCallId,
          toolName: evt.toolName,
          mcpServerName: evt.mcpServerName,
          toolTitle: evt.toolTitle,
          status: "running",
          argsText: evt.arguments,
        });
        // Always yield — sub-agent tool events are low-frequency (max 8 per inner
        // turn) and must render incrementally. The shouldYield throttle was causing
        // parallel tool-start events to batch (all arrive within <16ms from
        // Task.WhenAll execution) and only render on the next non-suppressed event.
        yield emit();
      }
      continue;
    }

    if (evt.type === "sub-agent-tool-progress") {
      const laneIdx = subAgentLaneIndexByRunId.get(evt.runId);
      if (laneIdx != null) {
        const lane = parts[laneIdx] as SubAgentLanePart;
        const tc = lane.toolCalls.find((t) => t.toolCallId === evt.toolCallId);
        if (tc) {
          tc.progressMessage = evt.message;
          yield emit();
        }
      }
      continue;
    }

    if (evt.type === "sub-agent-tool-result") {
      const laneIdx = subAgentLaneIndexByRunId.get(evt.runId);
      if (laneIdx != null) {
        const lane = parts[laneIdx] as SubAgentLanePart;
        const tc = lane.toolCalls.find((t) => t.toolCallId === evt.toolCallId);
        if (tc) {
          tc.status = evt.status;
          tc.progressMessage = undefined;
          tc.result = evt.resultPreview;
          yield emit();
        }
      }
      continue;
    }

    if (evt.type === "sub-agent-description") {
      const laneIdx = subAgentLaneIndexByRunId.get(evt.runId);
      if (laneIdx != null) {
        const lane = parts[laneIdx] as SubAgentLanePart;
        lane.description = evt.description;
        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "sub-agent-complete") {
      const laneIdx = subAgentLaneIndexByRunId.get(evt.runId);
      if (laneIdx != null) {
        const lane = parts[laneIdx] as SubAgentLanePart;
        lane.status = evt.status === "completed" ? "completed" : "failed";
        lane.answer = evt.answer;
        lane.summary = evt.summary;
        lane.error = evt.error;

        // Update process-window header with completed title.
        if (evt.completedTitle) {
          const reasoningPart = parts.find((p) => p.type === "reasoning") as
            | Extract<UiPart, { type: "reasoning" }>
            | undefined;
          if (reasoningPart) {
            reasoningPart.metadata = { ...reasoningPart.metadata, taskTitle: evt.completedTitle };
          }
        }

        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "doc-edit-start") {
      parts.push({
        type: "data",
        name: "doc-edit-cycle",
        editId: evt.editId,
        variant: evt.variant,
        documentName: evt.documentName,
        sourceToolCallId: evt.toolCallId,
        status: "running",
      } as DocEditCyclePart);
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "doc-edit-narration") {
      const cycle = parts.find(
        (p): p is DocEditCyclePart => p.type === "data" && (p as DocEditCyclePart).name === "doc-edit-cycle" && (p as DocEditCyclePart).editId === evt.editId,
      ) as DocEditCyclePart | undefined;
      if (cycle) {
        cycle.narration = evt.text;
        cycle.narrationPhase = evt.phase;
        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "doc-edit-complete") {
      const cycle = parts.find(
        (p): p is DocEditCyclePart => p.type === "data" && (p as DocEditCyclePart).name === "doc-edit-cycle" && (p as DocEditCyclePart).editId === evt.editId,
      ) as DocEditCyclePart | undefined;
      if (cycle) {
        cycle.status = "complete";
        cycle.summary = evt.summary;
        cycle.documentName = evt.documentName ?? cycle.documentName;
        cycle.patchCount = evt.patchCount;
        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "data-context-trimmed") {
      if (evt.data) {
        appEvents.emit("context-trimmed", evt.data as Record<string, unknown>);
      }
      continue;
    }

    if (evt.type === "data-thinking-task-title") {
      // Task title arrives after reasoning-end, so reasoningIndex is null.
      // Find the last reasoning part the same way reasoning-replace does.
      const classIdx = reasoningIndex ?? parts.findLastIndex((p) => p.type === "reasoning");
      if (evt.data && classIdx >= 0) {
        (parts[classIdx] as Extract<UiPart, { type: "reasoning" }>).metadata = evt.data;
        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "data-step-label") {
      const labelIdx = reasoningIndex ?? parts.findLastIndex((p) => p.type === "reasoning");
      if (evt.data && labelIdx >= 0) {
        const rp = parts[labelIdx] as Extract<UiPart, { type: "reasoning" }>;
        rp.stepLabel = evt.data.label;
        rp.stepIndex = evt.data.stepIndex;
        if (shouldYield()) yield emit();
      }
      continue;
    }

    if (evt.type === "data-usage-update") {
      capturedUsage = {
        inputTokens: evt.inputTokens,
        outputTokens: evt.outputTokens,
        reasoningTokens: evt.reasoningTokens,
        cachedTokens: evt.cachedTokens,
        totalTokens: evt.totalTokens,
        estimatedCostUsd: evt.estimatedCostUsd,
        estimatedCostDisplay: evt.estimatedCostDisplay,
        totalDurationMs: null,
      };
      if (shouldYield()) yield emit();
      continue;
    }

    if (evt.type === "finish") {
      // Safety: clear any leftover reconnecting state from auto-resume
      useReconnectStateStore.getState().setIdle(threadId);
      capturedUsage = {
        inputTokens: evt.inputTokens,
        outputTokens: evt.outputTokens,
        reasoningTokens: evt.reasoningTokens,
        cachedTokens: evt.cachedTokens,
        totalTokens: evt.totalTokens,
        estimatedCostUsd: evt.estimatedCostUsd,
        estimatedCostDisplay: evt.estimatedCostDisplay,
        totalDurationMs: evt.totalDurationMs,
      };
      // Stamp the donut store with the post-stream snapshot. ContextWindow on
      // FinishEvent reflects the just-finalized message's contribution, so the
      // donut updates the moment streaming ends without a follow-up fetch.
      const contextWindow = evt.contextWindow;
      if (threadId && contextWindow) {
        useContextBudgetStore.getState().set(threadId, contextWindow);
      }
      // Message completed — the cursor for this thread's in-flight message is
      // no longer useful. The NEXT message starts a new per-message queue with
      // its own counter, so leaving the stale cursor would mis-anchor resume.
      if (threadId) {
        clearLastEventId(threadId);
        appEvents.emit("turn-completed", { threadId });
      }
      break;
    }
  }
  } finally {
    // Always flush final state — even on abort (e.g. user navigates away),
    // emit the last accumulated snapshot so throttled updates aren't lost.
    yield emit();
  }

  // ─── Stream-end observability ─────────────────────────────────────────────
  const hasToolCallsPending = parts.some(
    (p) => p.type === "tool-call" && (p as { state?: string }).state === "pending",
  );

  if (hasToolCallsPending) {
    ClientLogger.error("SSE stream ended with pending tool calls", {
      component: "chatModelAdapters",
      action: "streamEnd",
      metadata: {
        threadId,
        finalStatus: messageStatus?.type ?? "unknown",
        pendingToolCount: parts.filter(
          (p) => p.type === "tool-call" && (p as { state?: string }).state === "pending",
        ).length,
      },
    });
  } else {
    ClientLogger.info("SSE stream ended", {
      component: "chatModelAdapters",
      action: "streamEnd",
      metadata: { threadId, finalStatus: messageStatus?.type ?? "unknown" },
    });
  }
}

export type ServerStreamEvent =
  | { type: "start"; modelId?: string; messageId?: string; startedAtUtc?: string }
  | { type: "text-delta"; textDelta?: string }
  | { type: "reasoning-start" }
  | { type: "reasoning-delta"; delta?: string }
  | { type: "reasoning-end" }
  | { type: "reasoning-replace"; text?: string }
  | { type: "tool-input-start"; toolCallId: string; toolName: string; mcpServerName?: string; toolTitle?: string }
  | { type: "tool-input-delta"; toolCallId: string; inputTextDelta: string }
  | { type: "tool-input-available"; toolCallId: string; toolName?: string; input?: unknown }
  | { type: "tool-result"; toolCallId: string; output: unknown }
  | { type: "tool-progress"; toolCallId: string; message: string; progress?: number; total?: number; isFinal?: boolean }
  | { type: "data-mcp-elicitation"; id: string; message: string; schema: unknown; context: unknown }
  | { type: "data-mcp-elicitation-resolved"; id: string; action: string }
  | { type: "data-tool-call-request"; data: ToolCallRequestData }
  | { type: "data-tool-call-canceled"; data: { id: string } }
  | { type: "data-tool-call-request-update"; data: { id: string; status: string } }
  | { type: "data-tool-call-result-inline"; data: { toolCallId: string; answer: string; isSkipped: boolean } }
  | { type: "data-document-citation"; data: DocumentCitationData }
  | { type: "data-web-source"; data: WebSourceData }
  | { type: "data-document-rendering"; documentId: string; order?: number; title?: string; versionId?: string; versionNumber?: number }
  | { type: "data-document-ready"; documentId: string; title?: string; versionId?: string; versionNumber?: number }
  | { type: "data-document-error"; documentId: string; error?: string; versionId?: string }
  | { type: "data-show-generated-document"; documentId: string; title?: string }
  | { type: "data-show-praetor-document"; praetorDocumentId: string }
  | { type: "data-pre-approval-review"; previewId: string; toolCallId: string; summary?: string; docxUrl?: string; pdfUrl?: string; diffHtmlUrl?: string; action?: string; documentName?: string; praetorDocumentId?: string; generatedDocumentId?: string }
  | { type: "data-title-generated"; data: { title: string; threadId?: string } }
  | { type: "data-context-trimmed"; data: unknown }
  | { type: "data-rate-limit-countdown"; data: { code: string; scope: string; retryAfterSec: number; message?: string } }
  | { type: "data-auto-resume-reconnecting"; data: { attempt: number; maxAttempts: number; backoffMs: number; reason: string } }
  | { type: "data-auto-resume-reconnected"; data: Record<string, never> }
  | { type: "data-praetor-revision-saved"; praetorDocumentId?: string; title?: string }
  | { type: "data-thinking-task-title"; data: ThinkingMetadata }
  | { type: "data-step-label"; data: { label: string; stepIndex: number } }
  | { type: "mcp-server-unavailable"; servers: { serverName: string; errorMessage: string }[] }
  | { type: "sub-agent-start"; runId: string; conversationId: string; agentType: string; query: string; locale: string }
  | { type: "sub-agent-tool-start"; runId: string; toolCallId: string; toolName: string; mcpServerName?: string; toolTitle?: string; arguments?: string }
  | { type: "sub-agent-tool-progress"; runId: string; toolCallId: string; message: string; progress?: number; total?: number }
  | { type: "sub-agent-tool-result"; runId: string; toolCallId: string; status: "success" | "error"; resultPreview?: string }
  | { type: "sub-agent-description"; runId: string; description: string }
  | { type: "sub-agent-complete"; runId: string; status: "completed" | "failed"; answer?: string; summary?: string; completedTitle?: string; error?: string }
  | { type: "doc-edit-start"; editId: string; variant: string; documentName?: string; toolCallId?: string }
  | { type: "doc-edit-narration"; editId: string; text: string; phase: string }
  | { type: "doc-edit-complete"; editId: string; summary: string; documentName?: string; patchCount?: number }
  | { type: "data-usage-update"; inputTokens?: number; outputTokens?: number; reasoningTokens?: number; cachedTokens?: number; totalTokens?: number; estimatedCostUsd?: number | null; estimatedCostDisplay?: string | null }
  | { type: "finish"; reason?: string; inputTokens?: number; outputTokens?: number; reasoningTokens?: number; cachedTokens?: number; totalTokens?: number; estimatedCostUsd?: number | null; estimatedCostDisplay?: string | null; totalDurationMs?: number | null; contextWindow?: ContextWindowSnapshot }
  | { type: "error"; code: string }
  | { type: "restored" };

export type DocEditCyclePart = {
  type: "data";
  name: "doc-edit-cycle";
  editId: string;
  variant: string;
  documentName?: string;
  sourceToolCallId?: string;
  narration?: string;
  narrationPhase?: string;
  summary?: string;
  patchCount?: number;
  status: "running" | "complete" | "error";
};

export type UiPart =
  | { type: "text"; text: string }
  | { type: "reasoning"; text: string; metadata?: ThinkingMetadata; stepLabel?: string; stepIndex?: number }
  | {
      type: "tool-call";
      toolCallId: string;
      toolName: string;
      argsText?: string;
      result?: unknown;
      state?: "partial" | "pending" | "complete";
      mcpServerName?: string;
      toolTitle?: string;
      progressMessage?: string;
      progressValue?: number;
      progressTotal?: number;
      isFinalProgress?: boolean;
    }
  | DocEditCyclePart
  | {
      type: "data";
      name: "sub-agent-lane";
      runId: string;
      conversationId: string;
      agentType: string;
      query: string;
      locale: string;
      status: "running" | "completed" | "failed";
      answer?: string;
      error?: string;
      description?: string;
      summary?: string;
      toolCalls: SubAgentToolEntry[];
    };

export type SubAgentToolEntry = {
  toolCallId: string;
  toolName: string;
  mcpServerName?: string;
  toolTitle?: string;
  status: "running" | "success" | "error";
  progressMessage?: string;
  argsText?: string;
  result?: string;
};

export type SubAgentLanePart = Extract<UiPart, { type: "data"; name: "sub-agent-lane" }>;

export type UiToolCallPart = Extract<UiPart, { type: "tool-call" }>;

type ToolCallPatch = Partial<Omit<UiToolCallPart, "type" | "toolCallId">>;

export type CreateChatModelAdapterDeps = {
  /** SSE decoder that yields your JSON events. `onEventId` fires once per SSE
   *  frame that carried an `id:` line — the caller stashes it so the next
   *  `/chat/resume` POST can send `Last-Event-ID`. */
  readJsonSse: (
    response: Response,
    abortSignal: AbortSignal,
    onEventId?: (id: string) => void,
  ) => AsyncGenerator<ServerStreamEvent>;

  /** Safe JSON parse helper. */
  safeJsonParse: (maybeJson: string) => unknown | undefined;
};


function parseSseDataLines(chunk: string): string[] {
  const lines = chunk.split(/\r?\n/);
  const out: string[] = [];
  for (const line of lines) {
     if (!line.startsWith("data:")) continue;

    // Allow optional single space after "data:"
    let payload = line.slice(5);

    out.push(payload); // no .trim()
  }
  return out;
}

export async function* readJsonSse(
  response: Response,
  abortSignal: AbortSignal,
  onEventId?: (id: string) => void,
): AsyncGenerator<ServerStreamEvent> {
  if (!response.body) throw new Error("No response body (stream expected).");

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  // SSE frames are separated by blank lines. `pendingId` rolls across lines
  // within a frame so `id:` correlates with the `data:` that follows it, and
  // resets on each frame boundary (per SSE spec: an event with no `id:` field
  // preserves the client's last-event-id; we emulate that by only reporting
  // when we see an `id:` line).
  let pendingId: string | null = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    if (abortSignal.aborted) {
      try {
        await reader.cancel();
      } catch {}
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    // Cut at the last SSE frame boundary (blank line: `\n\n` for LF or
    // `\r\n\r\n` for CRLF) so `complete` only ever contains whole frames.
    // Cutting at the nearest single `\n` would let a partial frame end with
    // `id: 42\n`, whose trailing-empty-string from the split would incorrectly
    // reset `pendingId` before the matching `data:` line arrived in the next
    // read — silently dropping the cursor for that event.
    const crlfBoundary = buffer.lastIndexOf("\r\n\r\n");
    const lfBoundary = buffer.lastIndexOf("\n\n");
    const cutAt = crlfBoundary >= 0 && crlfBoundary > lfBoundary
      ? crlfBoundary + 4
      : lfBoundary >= 0
        ? lfBoundary + 2
        : -1;
    if (cutAt === -1) continue;

    const complete = buffer.slice(0, cutAt);
    buffer = buffer.slice(cutAt);

    const lines = complete.split(/\r?\n/);
    for (const line of lines) {
      if (line === "") {
        // Frame separator — forget the id we saw in the previous frame.
        pendingId = null;
        continue;
      }

      if (line.startsWith(":")) {
        // SSE comment (e.g. ": heartbeat") — ignore.
        continue;
      }

      if (line.startsWith("id:")) {
        pendingId = line.slice(3).replace(/^ /, "");
        continue;
      }

      if (!line.startsWith("data:")) continue;

      const dataPayload = line.slice(5).replace(/^ /, "");
      if (!dataPayload) continue;
      if (dataPayload === "[DONE]") continue;

      // Emit the cursor BEFORE yielding so the caller's update order matches
      // "frame id seen" → "frame delivered". If JSON parse fails below, we
      // still advance the cursor — the server-side frame is real; only our
      // parse is broken, and we can't replay the broken frame usefully.
      if (pendingId !== null) onEventId?.(pendingId);

      try {
        yield JSON.parse(dataPayload) as ServerStreamEvent;
      } catch {
        // ignore malformed lines
      }
    }
  }
}

export function safeJsonParse(maybeJson: string): unknown | undefined {
  try {
    return JSON.parse(maybeJson);
  } catch {
    return undefined;
  }
}